import type { ComponentContentSchema } from '@vibesites/runtime';
import { RichTextContent } from '@vibesites/runtime/react';
import { clean, money, num, stagger } from '../utils/format';
import { useIsEditing, useRawProperty } from '../utils/hooks';
import { type FeeLine } from '../utils/fees';
import { feeHead, feeRow, rowAmt, rowApplied, rowFreq, rowMain, rowMeta, rowName } from '../utils/feeRows';

/** The pet fee rows on the Pet Policy page: one-time fee, pet rent, refundable deposit. */
export function Fees() {
  const p = useRawProperty();
  const editing = useIsEditing();

  const fee = num(clean(p.property_upfront_pet_fee)) || null;
  const rent = num(clean(p.property_monthly_pet_rent)) || null;
  const deposit = num(clean(p.property_pet_deposit)) || null;

  const rows: FeeLine[] = [];
  if (fee) rows.push({ name: 'Pet Fee', amount: money(fee)!, applied: 'Per pet', freq: 'One-time' });
  if (rent) rows.push({ name: 'Pet Rent', amount: money(rent)!, applied: 'Per pet', freq: 'Monthly' });
  if (deposit) rows.push({ name: 'Pet Deposit', amount: money(deposit)!, applied: 'Per pet', freq: 'Refundable' });

  if ((!p.property_ispetfriendly || !rows.length) && !editing) return null;

  return (
    <section className="section flex flex-col gap-10 max-[1199px]:gap-8 max-[767px]:gap-6" id="pet-fees">
      <div className="flex max-w-[760px] flex-col gap-4 max-[767px]:gap-3" data-reveal>
        <RichTextContent path="fees.eyebrow" as="div" className="eyebrow" />
        <RichTextContent path="fees.title" as="h2" className="h2" />
        <RichTextContent
          path="fees.lead"
          as="p"
          className="m-0 text-lg leading-7 text-muted-foreground [&_p]:m-0 max-[1199px]:text-[17px] max-[1199px]:leading-[27px] max-[767px]:text-[15px] max-[767px]:leading-[22px]"
        />
      </div>
      <div className="max-[767px]:border-t max-[767px]:border-t-[--q-border]" data-reveal style={stagger(1)}>
        <div className={feeHead} aria-hidden="true">
          <span>Fee</span>
          <span className="text-right">Amount</span>
          <span className="pl-10 max-[1199px]:pl-6">Applied</span>
          <span>Frequency</span>
        </div>
        {rows.map((l) => (
          <div className={feeRow} key={l.name}>
            <div className={rowMain}>
              <span className={rowName}>{l.name}</span>
              <span className={rowAmt}>{l.amount}</span>
              <span className={rowApplied}>{l.applied}</span>
              <span className={rowFreq}>{l.freq}</span>
              <span className={rowMeta}>{`${l.applied} · ${l.freq}`}</span>
            </div>
          </div>
        ))}
      </div>
      <RichTextContent
        path="fees.note"
        as="p"
        className="m-0 max-w-[680px] text-sm leading-5 text-muted-foreground [&_p]:m-0 max-[767px]:text-[13px] max-[767px]:leading-[19px]"
      />
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Fees',
  displayName: 'Pet Fees',
  tokens: {
    'fees.eyebrow': { label: 'Eyebrow', type: 'richtext', defaultValue: '<p>Pet fees</p>' },
    'fees.title': { label: 'Title', type: 'richtext', defaultValue: '<p>What your pets cost.</p>' },
    'fees.lead': {
      label: 'Lead',
      type: 'richtext',
      defaultValue: '<p>The fees below apply to each pet on the lease at <span data-type="field-token" data-field="property_name"></span>.</p>',
    },
    'fees.note': {
      label: 'Footnote',
      type: 'richtext',
      defaultValue: '<p>Pet fees are subject to change. Confirm current amounts with the leasing team before you apply.</p>',
    },
  },
};

(Fees as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Fees;
